/* eslint-disable no-underscore-dangle */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { AppProvider as PolarisProvider, Banner } from '@shopify/polaris';
import '@shopify/polaris/styles.css';
import RouteProvider from 'src/components/RouteProvider';
import { Helmet } from 'react-helmet';
import TenantProvider from 'src/components/TenantProvider';
import LoadingState from 'src/components/LoadingState';
import useQueryChecker from 'src/hooks/useQueryChecker';
import globalStyles from 'src/styles/global';
import PropTypes from 'prop-types';
import { useNavigate } from '@reach/router';
import useConfigureApp from './hooks/useConfigureApp';
import { callbackUrlGate, navigateTopLevel } from './utils/shopifyOAuth';

const Shopific = ({ children }) => {
  const { queryParams } = useQueryChecker();
  const [appConfigured, setAppConfigured] = useState(false);
  const [configureError, setConfigureError] = useState(null);
  const { configureApp, session } = useConfigureApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (queryParams.shop) {
      configureApp(queryParams.shop).then(({ redirect, callbackURL }) => {
        const gate = callbackUrlGate(callbackURL, queryParams.shop);
        if (gate.action === 'navigate') {
          navigateTopLevel(gate.url);
          return;
        }
        if (gate.action === 'stop') {
          setConfigureError(gate.message);
          return;
        }
        const path = redirect || '/home';
        navigate(path, { replace: true });
        setAppConfigured(true);
      });
    }
  }, [configureApp, navigate, queryParams.shop]);

  if (configureError) {
    return (
      <PolarisProvider>
        <Banner status="critical" title="Install link blocked">
          <p>{configureError}</p>
        </Banner>
      </PolarisProvider>
    );
  }

  return !queryParams || !appConfigured ? (
    <PolarisProvider>
      <LoadingState />
    </PolarisProvider>
  ) : (
    <>
      <Helmet>
        <title>Thinkific Shopify App</title>
        <meta charSet="utf-8" />
        <link
          href="https://fonts.googleapis.com/css?family=Barlow:300,400&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <PolarisProvider>
        <TenantProvider currentSession={session} queryParams={queryParams}>
          <RouteProvider>{children}</RouteProvider>
        </TenantProvider>
      </PolarisProvider>
      <style global jsx>
        {globalStyles}
      </style>
    </>
  );
};

Shopific.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default Shopific;
